import {
  createElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type UIEvent,
} from 'react';
import { cn } from '../lib/utils';
import {
  columnLetter,
  looksNumeric,
  type DocumentPreview,
  type SpreadsheetPreviewSheet,
} from '../lib/document-preview';

/**
 * Renderers for the read-only office previews (ADR-0033): a spreadsheet grid
 * with sheet tabs, Word HTML (through {@link SafeHtml}), text, or rendered
 * pages (a TIFF scan, a HEIC photo). Shared by the admin preview and the buyer
 * room's view-only viewer; the caller owns the chrome (title bar, Download /
 * "View only", watermark) and the `unsupported` fallback.
 */

const fmt = (n: number) => n.toLocaleString('en-US');

export interface DocumentPreviewViewProps {
  /** A preview of kind spreadsheet / html / text (`unsupported` is the caller's fallback). */
  preview: Exclude<DocumentPreview, { kind: 'unsupported' }>;
  /** Accessible name for the content area (the document name). */
  title: string;
  className?: string;
  /** Rendered pages only: called with the page in view (page-level reading, ADR-0056). */
  onPageChange?: (page: number, numPages: number) => void;
}

export function DocumentPreviewView({
  preview,
  title,
  className,
  onPageChange,
}: DocumentPreviewViewProps) {
  if (preview.kind === 'spreadsheet') {
    return (
      <SpreadsheetPreview
        sheets={preview.sheets}
        sheetsTruncated={preview.sheetsTruncated}
        title={title}
        className={className}
      />
    );
  }
  if (preview.kind === 'pages') {
    return (
      <PagesPreview
        pages={preview.pages}
        totalPages={preview.totalPages}
        pagesTruncated={preview.pagesTruncated}
        title={title}
        className={className}
        onPageChange={onPageChange}
      />
    );
  }
  if (preview.kind === 'html') {
    return (
      <div className={cn('overflow-auto bg-muted/30 p-4', className)}>
        <article
          aria-label={title}
          data-preview="document"
          className={cn(
            'mx-auto max-w-3xl rounded border bg-background px-8 py-6 text-sm leading-relaxed text-foreground shadow-sm',
            // Word-like typography for mammoth's plain structural HTML.
            '[&_h1]:mb-3 [&_h1]:mt-5 [&_h1]:text-xl [&_h1]:font-semibold',
            '[&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-semibold',
            '[&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:font-semibold [&_h4]:font-semibold [&_h5]:font-semibold [&_h6]:font-semibold',
            '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6',
            '[&_table]:my-3 [&_table]:border-collapse [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:font-semibold',
            '[&_img]:my-2 [&_img]:inline-block [&_img]:max-w-full [&_blockquote]:border-l-2 [&_blockquote]:pl-3',
          )}
        >
          <SafeHtml html={preview.html} />
        </article>
        {preview.imagesOmitted ? (
          <p className="mx-auto mt-2 max-w-3xl text-xs text-muted-foreground">
            Some images in this document aren’t shown in the preview.
          </p>
        ) : null}
        {preview.truncated ? (
          <p className="mx-auto mt-2 max-w-3xl text-xs text-muted-foreground">
            The preview shows the beginning of this document only.
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <div className={cn('flex flex-col overflow-auto bg-muted/30 p-4', className)}>
      <pre
        aria-label={title}
        className="w-full flex-1 whitespace-pre-wrap break-words rounded border bg-background p-4 text-left text-xs leading-relaxed"
      >
        {preview.text}
      </pre>
      {preview.truncated ? (
        <p className="mt-2 text-xs text-muted-foreground">
          The preview shows the beginning of this document only.
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rendered pages (TIFF scans, HEIC photos)
// ---------------------------------------------------------------------------

/** Only an inline JPEG the server rendered: no network fetch can start from a preview. */
const SAFE_PAGE_SRC = /^data:image\/jpeg;base64,[a-z0-9+/=]+$/i;

export interface PagesPreviewProps {
  pages: Extract<DocumentPreview, { kind: 'pages' }>['pages'];
  totalPages: number;
  pagesTruncated: boolean;
  title: string;
  className?: string;
  /** Called with the page in view (the last one whose top is above the upper
   * third), once on open and on every change. */
  onPageChange?: (page: number, numPages: number) => void;
}

/**
 * The pages stacked top to bottom, each scaled to the viewer's width (never
 * enlarged past its rendered size), not draggable. A note says when only the
 * first pages are shown.
 */
export function PagesPreview({
  pages,
  totalPages,
  pagesTruncated,
  title,
  className,
  onPageChange,
}: PagesPreviewProps) {
  const shown = pages.filter((page) => SAFE_PAGE_SRC.test(page.src));
  const [current, setCurrent] = useState(1);
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  useEffect(() => {
    if (shown.length > 0) onPageChangeRef.current?.(current, Math.max(totalPages, shown.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, totalPages, shown.length]);
  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const probe = el.scrollTop + el.clientHeight / 3;
    let page = 1;
    el.querySelectorAll<HTMLElement>('[data-preview-page]').forEach((img, i) => {
      if (img.offsetTop <= probe) page = i + 1;
    });
    setCurrent(page);
  };
  return (
    <div
      className={cn('overflow-auto bg-muted/30 p-4', className)}
      data-preview="pages"
      onScroll={onScroll}
    >
      <div aria-label={title} role="group" className="mx-auto flex max-w-4xl flex-col items-center gap-4">
        {shown.map((page, i) => (
          <img
            key={i}
            data-preview-page={i + 1}
            src={page.src}
            width={page.width}
            height={page.height}
            alt={shown.length > 1 ? `${title} — page ${i + 1} of ${totalPages}` : title}
            draggable={false}
            className="h-auto max-w-full rounded border bg-background shadow-sm"
          />
        ))}
      </div>
      {pagesTruncated ? (
        <p role="note" className="mx-auto mt-3 max-w-4xl text-xs text-muted-foreground">
          {`Showing the first ${fmt(shown.length)} of ${fmt(totalPages)} pages.`}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spreadsheet grid
// ---------------------------------------------------------------------------

export interface SpreadsheetPreviewProps {
  sheets: SpreadsheetPreviewSheet[];
  sheetsTruncated: boolean;
  title: string;
  className?: string;
}

/**
 * A read-only grid per sheet: sticky column letters and row numbers (Excel
 * aligned), amounts right-aligned, sheet tabs, and a plain note when a sheet
 * was cut at the preview's row/column cap.
 */
export function SpreadsheetPreview({
  sheets,
  sheetsTruncated,
  title,
  className,
}: SpreadsheetPreviewProps) {
  const [active, setActive] = useState(0);
  const sheet = sheets[Math.min(active, sheets.length - 1)];

  return (
    <div className={cn('flex min-h-0 flex-col bg-background', className)}>
      {sheets.length > 1 ? (
        <div
          role="tablist"
          aria-label="Sheets"
          className="flex shrink-0 gap-1 overflow-x-auto border-b bg-muted/40 px-2 pt-2"
        >
          {sheets.map((s, i) => (
            <button
              key={`${i}-${s.name}`}
              type="button"
              role="tab"
              aria-selected={i === active}
              onClick={() => setActive(i)}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-t border border-b-0 px-3 py-1 text-xs',
                i === active
                  ? 'bg-background font-semibold text-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground',
              )}
            >
              {s.name}
              {s.hidden ? <span className="ml-1 font-normal italic">(hidden)</span> : null}
            </button>
          ))}
        </div>
      ) : null}
      {sheet ? <SheetGrid key={active} sheet={sheet} title={title} /> : <EmptySheet />}
      <SheetNotes sheet={sheet} sheetsTruncated={sheetsTruncated} />
    </div>
  );
}

function EmptySheet() {
  return (
    <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
      This sheet is empty.
    </div>
  );
}

function SheetGrid({ sheet, title }: { sheet: SpreadsheetPreviewSheet; title: string }) {
  const columns = useMemo(
    () => sheet.rows.reduce((max, row) => Math.max(max, row.length), 0),
    [sheet.rows],
  );
  if (sheet.rows.length === 0 || columns === 0) return <EmptySheet />;

  return (
    <div className="min-h-0 flex-1 overflow-auto" data-preview="spreadsheet">
      <table
        aria-label={`${title} — ${sheet.name}`}
        className="border-separate border-spacing-0 text-xs"
      >
        <thead>
          <tr>
            <th
              aria-hidden="true"
              className="sticky left-0 top-0 z-20 min-w-[3rem] border-b border-r bg-muted"
            />
            {Array.from({ length: columns }, (_, c) => (
              <th
                key={c}
                scope="col"
                className="sticky top-0 z-10 min-w-[4rem] border-b border-r bg-muted px-2 py-1 text-center font-medium text-muted-foreground"
              >
                {columnLetter(sheet.startColumn + c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sheet.rows.map((row, r) => (
            <tr key={r}>
              <th
                scope="row"
                className="sticky left-0 z-10 border-b border-r bg-muted px-2 py-1 text-right font-medium text-muted-foreground"
              >
                {sheet.startRow + r + 1}
              </th>
              {Array.from({ length: columns }, (_, c) => {
                const text = row[c] ?? '';
                return (
                  <td
                    key={c}
                    title={text.length > 40 ? text : undefined}
                    className={cn(
                      'max-w-[28rem] truncate whitespace-nowrap border-b border-r px-2 py-1',
                      (sheet.numeric ? sheet.numeric[r]?.[c] === '1' : looksNumeric(text)) &&
                        'text-right tabular-nums',
                    )}
                  >
                    {text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SheetNotes({
  sheet,
  sheetsTruncated,
}: {
  sheet: SpreadsheetPreviewSheet | undefined;
  sheetsTruncated: boolean;
}) {
  const notes: string[] = [];
  if (sheet?.rowsTruncated) {
    notes.push(
      `Showing the first ${fmt(sheet.startRow + sheet.rows.length)} of ${fmt(sheet.totalRows)} rows.`,
    );
  }
  if (sheet?.columnsTruncated) {
    notes.push(`Showing the first ${fmt(sheet.rows[0]?.length ?? 0)} of ${fmt(sheet.totalColumns)} columns.`);
  }
  if (sheetsTruncated) notes.push('Only the first sheets of this workbook are shown.');
  if (notes.length === 0) return null;
  return (
    <p role="note" className="shrink-0 border-t bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
      {notes.join(' ')}
    </p>
  );
}

// ---------------------------------------------------------------------------
// SafeHtml — allowlist rebuild of untrusted HTML into React elements
// ---------------------------------------------------------------------------

/** Tags rendered as themselves (no attributes unless listed in ALLOWED_ATTRIBUTES). */
const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'span',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'img',
]);
/** Tags dropped WITH their content (never text a reader should see as the document). */
const DROPPED_TAGS = new Set([
  'script', 'style', 'template', 'noscript', 'iframe', 'frame', 'frameset', 'object', 'embed',
  'svg', 'math', 'head', 'title', 'meta', 'link', 'base', 'form', 'input', 'button', 'select',
  'textarea', 'audio', 'video', 'source', 'canvas',
]);
const VOID_TAGS = new Set(['br', 'hr', 'img']);
/** Raster images only, inline only: no network fetch can ever start from a preview. */
const SAFE_IMAGE_SRC = /^data:image\/(png|jpeg|gif|webp|bmp);base64,[a-z0-9+/=\s]+$/i;
const MAX_DEPTH = 64;

function span(value: string | null): number | undefined {
  const n = Number.parseInt(value ?? '', 10);
  return Number.isFinite(n) && n > 1 ? Math.min(n, 1000) : undefined;
}

function rebuild(node: Node, key: number, depth: number): ReactNode {
  if (node.nodeType === 3 /* TEXT_NODE */) return node.textContent;
  if (node.nodeType !== 1 /* ELEMENT_NODE */ || depth > MAX_DEPTH) return null;
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (DROPPED_TAGS.has(tag)) return null;
  const children = () =>
    Array.from(el.childNodes, (child, i) => rebuild(child, i, depth + 1));

  // Anything not allowlisted (links included) is unwrapped: its text stays, the element goes.
  if (!ALLOWED_TAGS.has(tag)) return createElement('span', { key }, ...children());

  if (tag === 'img') {
    const src = el.getAttribute('src') ?? '';
    if (!SAFE_IMAGE_SRC.test(src)) return null;
    return createElement('img', { key, src, alt: el.getAttribute('alt') ?? '', draggable: false });
  }
  const props: Record<string, unknown> = { key };
  if (tag === 'td' || tag === 'th') {
    props.colSpan = span(el.getAttribute('colspan'));
    props.rowSpan = span(el.getAttribute('rowspan'));
  }
  if (VOID_TAGS.has(tag)) return createElement(tag, props);
  return createElement(tag, props, ...children());
}

/**
 * Renders untrusted HTML (a Word document converted server-side) WITHOUT ever
 * handing it to `innerHTML`: it is parsed into an inert document (`DOMParser`
 * runs no scripts and loads nothing), then rebuilt as React elements from an
 * allowlist — structural tags only, no attributes except table spans and an
 * inline raster `data:` image `src`. Links, styles, event handlers, forms and
 * embedded content cannot survive the rebuild; text is escaped by React.
 */
export function SafeHtml({ html }: { html: string }) {
  const content = useMemo(() => {
    if (typeof DOMParser === 'undefined') return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.body.childNodes, (node, i) => rebuild(node, i, 0));
  }, [html]);
  return <>{content}</>;
}
